import { useCallback, useState } from 'react';
import { Button, Typography } from '@mui/material';

import useOpenAccountingSetupHelp, { AccountingSetupHelpContext } from '../hooks/useOpenAccountingSetupHelp';

import SparklesOutlineIcon from 'modules/common/components/Icons/SparklesOutlineIcon';
import useStatsigGate from 'modules/common/useStatsigGate';
import { FeatureGates } from 'modules/common/constants/featuregates';
import useIMC from 'modules/IMC/useIMC';

const DEFAULT_TITLE = 'Why is this important';

interface HelpLinkButtonProps {
    title: string;
    size: 'small' | 'medium';
    onClick: () => void;
    /** Only the setup path can be mid-flight; the other two open synchronously. */
    isOpening?: boolean;
}

/**
 * The link itself, with no opinion on what clicking it does.
 *
 * Separate from the three variants below so the markup has one definition — the gate must change
 * where the question goes, never how the link looks.
 */
function HelpLinkButton({ title, size, onClick, isOpening = false }: Readonly<HelpLinkButtonProps>) {
    return (
        <Button
            variant="text"
            color="primary"
            onClick={onClick}
            disabled={isOpening}
            sx={{
                p: 0,
                minWidth: 'auto',
                '&:hover': {
                    backgroundColor: 'transparent',
                },
            }}>
            <SparklesOutlineIcon sx={{ fontSize: 16, mr: 0.5 }} />
            <Typography variant={size === 'small' ? 'caption' : 'body2'} color="primary">
                {title}
            </Typography>
        </Button>
    );
}

interface HelpLinkVariantProps {
    question: string;
    title: string;
    size: 'small' | 'medium';
}

/**
 * Gate off: the member's question goes to the in-app help agent widget, with no conversation
 * created and no metadata, so the default AOP answers it.
 *
 * Reads only IMC context. Keeping it out of the gated variant is the point of the split — the
 * setup hook pulls in auth, the member query and two mutations, and none of that should be a
 * dependency of a leaf link on a step where the gate is off.
 */
function GenericHelpLink({ question, title, size }: Readonly<HelpLinkVariantProps>) {
    const { openHelpAgent } = useIMC();

    const handleClick = useCallback(() => {
        openHelpAgent({ message: question });
    }, [openHelpAgent, question]);

    return <HelpLinkButton title={title} size={size} onClick={handleClick} />;
}

/**
 * Gate on: a conversation is created server-side carrying the routing metadata, then handed to
 * the Decagon popup — see useOpenAccountingSetupHelp for why that order matters.
 *
 * Owns the in-flight guard because it is the only variant that awaits a round trip; a second
 * click before the popup opens would create a second conversation.
 */
function SetupHelpLink({
    question,
    context,
    title,
    size,
}: Readonly<HelpLinkVariantProps & { context: AccountingSetupHelpContext }>) {
    const openSetupHelp = useOpenAccountingSetupHelp({ question, context });
    const [isOpening, setIsOpening] = useState(false);

    const handleClick = useCallback(async () => {
        if (isOpening) {
            return;
        }
        setIsOpening(true);
        try {
            await openSetupHelp();
        } finally {
            setIsOpening(false);
        }
    }, [isOpening, openSetupHelp]);

    return <HelpLinkButton title={title} size={size} onClick={handleClick} isOpening={isOpening} />;
}

interface WhyImportantLinkProps {
    /**
     * Which wizard step this link sits on. Required so a new entrypoint can't silently report as
     * another step — per-question reporting is the reason the entrypoint exists (AP-2448).
     */
    context: AccountingSetupHelpContext;
    /**
     * The question sent to the agent, when it should differ from the visible `title`. Defaults to
     * the title, which is already phrased as the member's question in the design.
     */
    helpQuestion?: string;
    /** Visible hint text. Varies per question — this is what the member is asking. */
    title?: string;
    /**
     * Escape hatch for a caller that must handle the click itself. Wins over both variants, so a
     * call site that passes it opts out of the setup AOP metadata entirely — prefer `helpQuestion`.
     *
     * Decides who handles the click, NOT whether there is a link to click: `requiresSetupAgent`
     * is checked first and still hides the link gate-off. Reversing that would show a gate-off
     * member an entrypoint AP-2448 added, which is the thing that prop exists to prevent.
     */
    onClick?: () => void;
    /**
     * `small` (12px) sits inline on a section header beside its title; `medium` (14px) is the
     * page-level hint under a step's intro copy. Both sizes are in the designs — see the section
     * headers in node 8279:48798 against the overview hint in node 9300:43609.
     */
    size?: 'small' | 'medium';
    /**
     * Whether this entrypoint exists only while the setup agent does.
     *
     * The links that predate AP-2448 have a gate-off home — the generic widget — so they render
     * either way and only their channel changes. The ones AP-2448 introduced have no gate-off
     * behaviour to fall back to: they were added *because* the setup agent can answer them, so
     * with the gate off they must leave no trace, or flipping it off stops being a return to
     * today's product.
     *
     * Set it on a call site added by AP-2448; leave it off on one that was already there.
     */
    requiresSetupAgent?: boolean;
}

/**
 * Picks which of the three variants above handles the click, and does nothing else.
 *
 * The branch is a component boundary rather than an `if` inside one handler so that each path
 * mounts only the hooks it needs: gate-off never touches the setup hook's providers, and the
 * `onClick` escape hatch touches neither.
 *
 * Reads the gate here rather than at each call site so the whole AP-2448 rollout answers to one
 * gate read — both what the click does and, via `requiresSetupAgent`, whether the link is there
 * to click. AccountingProgress is the one exception: it swaps in a help-centre article when the
 * gate is off rather than rendering nothing, so it owns that ternary itself.
 */
export default function WhyImportantLink({
    context,
    helpQuestion = '',
    title = DEFAULT_TITLE,
    onClick = undefined,
    size = 'small',
    requiresSetupAgent = false,
}: Readonly<WhyImportantLinkProps>) {
    const isSetupEntrypointEnabled = useStatsigGate(FeatureGates.AccountingSetupHelpEntrypoint);

    // The visible hint doubles as the question when a caller gives no more specific one — the two
    // are the same sentence in the design, and sending it verbatim is what lets the AOP answer
    // the question behind this entrypoint rather than the step in general. Shared by both
    // variants: gate-off asks the same thing through a different channel, it does not ask less.
    const question = helpQuestion || title;

    // Before the `onClick` check, deliberately. Existence is settled before behaviour, so a call
    // site that passes both still disappears with the gate rather than rendering an AP-2448
    // entrypoint to a gate-off member.
    if (requiresSetupAgent && !isSetupEntrypointEnabled) {
        return null;
    }

    if (onClick) {
        return <HelpLinkButton title={title} size={size} onClick={onClick} />;
    }

    if (!isSetupEntrypointEnabled) {
        return <GenericHelpLink question={question} title={title} size={size} />;
    }

    return <SetupHelpLink question={question} context={context} title={title} size={size} />;
}
